'use client';

import clsx from 'clsx';
import AppLink from '@/components/i18n/AppLink';
import type { NavCategoryGroup } from '@/lib/routing/navTree';
import { H3, Li, P, Ul, surfaceCardInteractive } from '@/components/ui';

export type NavCategoryPanelProps = {
  group: NavCategoryGroup;
  description?: string;
  /** `compact` = smaller link list; `full` = show all children */
  density?: 'compact' | 'full';
  className?: string;
};

const MAX_COMPACT_LINKS = 8;

const NavCategoryPanel: React.FC<NavCategoryPanelProps> = ({
  group,
  description,
  density = 'full',
  className,
}) => {
  const links =
    density === 'compact' ? group.children.slice(0, MAX_COMPACT_LINKS) : group.children;
  const overflow = density === 'compact' ? group.children.length - links.length : 0;

  return (
    <article
      className={surfaceCardInteractive(clsx('flex h-full min-w-0 flex-col', className))}
    >
      <H3 className="m-0 text-base font-semibold text-prose">
        <AppLink href={group.href} className="hover:underline underline-offset-4">
          {group.title}
        </AppLink>
      </H3>
      {description ? (
        <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{description}</P>
      ) : null}
      <Ul className="m-0 mt-4 flex-1 list-none space-y-1.5 p-0">
        {links.map((link) => (
          <Li key={link.href}>
            <AppLink
              href={link.href}
              className="block break-words text-sm text-prose-muted hover:text-prose underline-offset-2 hover:underline"
            >
              {link.label}
            </AppLink>
          </Li>
        ))}
        {overflow > 0 ? (
          <Li>
            <AppLink
              href={group.href}
              className="text-xs font-medium text-[var(--color-primary)] hover:underline"
            >
              +{overflow} more
            </AppLink>
          </Li>
        ) : null}
      </Ul>
    </article>
  );
};

export default NavCategoryPanel;
